import React, { forwardRef, useState, useMemo, useCallback, useRef, useEffect } from 'react';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import _, { split, uniqBy, isEmpty, map, find } from 'lodash';
import { FieldTypes, generateFormFields } from 'utils/formUtils';
import { findOptionsFromMap } from '../GenericMappingForm';
import { Alert, Button, Form, Tabs, Typography, message } from 'antd';
import { MODAL_SIZE_LARGE } from 'constants/Layout';
import SubProductAPI from 'restAPI/SubProductAPI';
import SubProductEnvironmentTypeAPI from 'restAPI/SubProductEnvironmentTypeAPI';
import ProductAPI from 'restAPI/ProductAPI';
import { IntegrationType } from '../common';
import BaseModal from 'components/BaseModal';
import Vocabulary from 'components/Vocabulary';
import { ArrowDownOutlined, PlusOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { DeleteIcon } from 'components/Icons';
import { uniq } from 'utils/lodashUtils';
import { PermissionSetMap } from 'constants/RBACConstants';
import { hasPermission } from 'utils/authService';
import { useHasDisabledEnvironment } from 'contexts/GlobalSettingsContext';

const { TabPane } = Tabs;

const PARAM_SCOPE_FIELD = { type: "scope" };

const initialFormValues = {
  configurations: [{
      product: undefined,
      subProduct: undefined,
      environment: undefined,
      repoName: undefined,
      imageSelectAll: false,
      vmSelectAll: false,
      fnSelectAll: false,
      accountSelectAll: false,
  }]
}

const commonAquasecFieldsProperty = {
  labelCol: { span: 24 },
  wrapperCol: { span: 24 },
}

const AquasecMappingForm = forwardRef(function AquasecMappingForm({disableProjectFields = false, ...props}, ref) {
  const [form] = Form.useForm();
  const editMode = !isEmpty(props.record);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subProductsMap, setSubProductsMap] = useState({});
  const [envMap, setEnvMap] = useState({});
  const disableMapping = editMode && !props.isUnmapped;
  const [mappedProjectList] = useState(() => {
    // this state contains the already mapped projects
    // This is stored because the project and version dropdowns are paginated
    // and if the option selected is not on 1st page, then the ID is displayed as this is how select works
    if (props.record) {
      const { imageId, imageName, scope, vmId, vmName, functionId, functionName, groupId, accountId, accountName } = props.record;
      if(props.record){
        const imageIdArray = split(imageId, ',');
        const imageNameArray = split(imageName, ',');
        const vmIdArray = split(vmId, ",");
        const vmNameArray = split(vmName, ",");
        const functionIdArray = split(functionId, ",");
        const functionNameArray = split(functionName, ",");
        const accountIdArray = split(accountId, ",");
        const accountNameArray = split(accountName, ",");
          return {
            projects: map(imageIdArray, (id, index) => ({id, name: imageNameArray[index]})),
            scope: scope ? [{ id: scope, name: scope }] : [],
            vm: map(vmIdArray, (id, index) => ({id, name: vmNameArray[index]})),
            fn: map(functionIdArray, (id, index) => ({id, name: functionNameArray[index]})),
            group: groupId ? [{id: groupId, name: groupId}] : [],
            account: map(accountIdArray, (id, index) => ({id, name: accountNameArray[index]}))
          }
      }
    }
    return { projects: [], scope: [], vm: [], fn: [] }
  })

  const [options, setOptions] = useState(mappedProjectList.projects);
  const [currentTab, setCurrentTab] = useState("image");
  const [errorFields, setErrorFields] = useState();
  const apiParamsRef = useRef({});
  const vmApiParamRef = useRef({});
  const fnApiParamRef = useRef({});
  const isOnPrem = props.loginConfig?.isOnPrem === "true" || props.isOnPrem;
  const {hasDisabledEnvironment, envValue} = useHasDisabledEnvironment();
  const setOptionsFromPagination = useCallback((option) => {
    setOptions(prev => uniqBy([...prev, ...option], 'id'));
  },[])

  const fetchProjects = useCallback((params) => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, ...params }, false);
    promise.then((response) => {
      if (mappedProjectList['projects'].length > 0 && !isEmpty(response.projects)) {
        if (params.type === PARAM_SCOPE_FIELD.type) {
          response.projects = [...response.projects, ...mappedProjectList['scope']];
        } else {
          response.projects = [...response.projects, ...mappedProjectList['projects']];
        }
      }
      setOptionsFromPagination(response.projects)
    }).catch((e)=>{
      //need to check permision issue only for Aqua Image field only 
      if(e && !params.type){
        const errMsg = e.response?.data?.message;
        if(e.response?.status === 400 && errMsg?.includes("403")){
          setErrorFields(prevData => uniq([...(prevData || []), "Aqua Image"]));
        }
      }
    });
    return { promise, cancel };
  }, [props.toolName, mappedProjectList, props.loginConfigId, setOptionsFromPagination]);

  const fetchVM = useCallback((params) => {
    if(!isOnPrem && !params.project){
      return {promise: Promise.resolve([]), cancel: _.noop};
    }
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, params, false);
    promise.then((response) =>{
      if (mappedProjectList['vm'].length > 0 && !isEmpty(response.projects)) {
        response.projects = [...response.projects, ...mappedProjectList['vm']]
      }
      setOptionsFromPagination(response.projects, params.type)
    }).catch((e)=>{
      if(e){
        const errMsg = e.response?.data?.message;
        if(e.response?.status === 400 && errMsg?.includes("403")){
          setErrorFields(prevData => uniq([...(prevData || []), "VM Names"]));
        }
      }
    });
    return { promise, cancel }
  }, [props.toolName, setOptionsFromPagination, isOnPrem, mappedProjectList]);

  const fetchFunctions = useCallback((params)=>{
    if(!isOnPrem && !params.project){
      return {promise: Promise.resolve([]), cancel: _.noop};
    }
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, params, false);
    promise.then((response) =>{
      if (mappedProjectList['fn'].length > 0 && !isEmpty(response.projects)) {
        response.projects = [...response.projects, ...mappedProjectList['fn']]
      }
      setOptionsFromPagination(response.projects, params.type)
    }).catch((e)=>{
      if(e){
        const errMsg = e.response?.data?.message;
        if(e.response?.status === 400 && errMsg?.includes("403")){
          setErrorFields(prevData => uniq([...(prevData || []), "Function Names"]));
        }
      }
    })
    return { promise, cancel }
  }, [props.toolName, setOptionsFromPagination, isOnPrem, mappedProjectList]);

  const getGroups = useCallback((params)=>{
    if(isOnPrem){
      return {promise: Promise.resolve([]), cancel: _.noop};
    }
    params = {...params, login_id: props.loginConfigId, type:"group"}
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, params);
    promise.then((response) =>{
      if (mappedProjectList['group']?.length > 0 && !isEmpty(response.projects)) {
        response.projects = [...response.projects, ...mappedProjectList['group']]
      }
      setOptionsFromPagination(response.projects, params.type)
    })
    return { promise, cancel }
  }, [props.toolName, setOptionsFromPagination, isOnPrem, mappedProjectList, props.loginConfigId]);

  const getCloudAccounts = useCallback((params)=>{
    if((params.project) || (props.record && !isEmpty(_.compact(mappedProjectList["account"])))){
      const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, params);promise.then((response) =>{
        if (mappedProjectList['account']?.length > 0 && !isEmpty(response.projects)) {
          response.projects = [...response.projects, ...mappedProjectList['account']]
        }
        setOptionsFromPagination(response.projects, params.type)
      })
      return { promise, cancel }
    }
    return {promise: Promise.resolve([]), cancel: _.noop};
  }, [props.toolName, setOptionsFromPagination, mappedProjectList, props.record]);

  const handleTabChange = useCallback((tab) => setCurrentTab(tab), []);

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    return { 
      imageId: config.imageId === "All" ? [] : !_.isEmpty(config.imageId) ? config.imageId.split(',') : undefined,
    vmId: config.vmId === "All" ? [] : config.vmId?.split(","),
    functionId: config.functionId === "All" ? [] : config.functionId?.split(","),
    imageSelectAll: config.imageId === "All" ? true : false,
    accountId: config.accountId === "All" ? [] : config.accountId?.split(","),
    vmSelectAll: config.vmId === "All" ? true : false,
    fnSelectAll: config.functionId === "All" ? true : false,
    accountSelectAll: config.accountId === "All" ? true: false,
    //send the property received in config
    ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
   };
  }, []);

  const onScopeChange = useCallback((value, field) => {
    const { name } = field;
    const configurations = form.getFieldValue("configurations");
    const updatedConfiguration = [
      ...configurations,
    ];
    updatedConfiguration[name] = {
      ...updatedConfiguration[name],
      imageId: undefined,
    }
    form.setFieldsValue({ configurations: updatedConfiguration });
  }, [form]);

  const onGroupChange = useCallback((value, field) => {
    const { name } = field;
    const configurations = form.getFieldValue("configurations");
    const updatedConfiguration = [
      ...configurations,
    ];
    updatedConfiguration[name] = {
      ...updatedConfiguration[name],
      accountId: undefined,
    }
    form.setFieldsValue({ configurations: updatedConfiguration });
  }, [form]);

  const onOptionalFieldsChange = useCallback((value, field) => {
    const { name } = field || {};
    const configurations = form.getFieldValue("configurations");
    const updatedConfiguration = [
      ...configurations,
    ];
    updatedConfiguration[name] = {
      ...updatedConfiguration[name],
      imageSelectAll: !isEmpty(updatedConfiguration[name].imageId) ? false : updatedConfiguration[name].imageSelectAll,
      vmSelectAll: !isEmpty(updatedConfiguration[name].vmId) ? false : updatedConfiguration[name].vmSelectAll,
      fnSelectAll: !isEmpty(updatedConfiguration[name].functionId) ? false : updatedConfiguration[name].fnSelectAll,
      accountSelectAll: !isEmpty(updatedConfiguration[name].accountId) ? false : updatedConfiguration[name].accountSelectAll
    }
    form.setFieldsValue({ configurations: updatedConfiguration });
  }, [form]);

  const onOptionalFieldsAllSelectChange = useCallback((e)=>{
    if(!e.target){
      return;
    }
    const { name } = e.target.listField || {};
    const configurations = form.getFieldValue("configurations");
    const updatedConfiguration = [...configurations];
    updatedConfiguration[name] = {
      ...updatedConfiguration[name],
      imageId: updatedConfiguration[name].imageSelectAll === true ? [] : updatedConfiguration[name].imageId,
      vmId: updatedConfiguration[name].vmSelectAll === true ? [] : updatedConfiguration[name].vmId,
      functionId: updatedConfiguration[name].fnSelectAll === true ? [] : updatedConfiguration[name].functionId,
      accountId: updatedConfiguration[name].accountSelectAll === true ? [] : updatedConfiguration[name].accountId
    }
    form.setFieldsValue({ configurations: updatedConfiguration });
  },[form]);

  const getExtraFieldsData = useCallback((config) => {
    const { imageId, vmId, functionId, imageSelectAll, vmSelectAll, fnSelectAll, groupId, accountId, accountSelectAll } = config;
    const data = options.reduce((acc,cv) =>{
      const { id, name } = cv;
      if(imageId?.includes(id)){
        acc.id.push(id);
        acc.name.push(name);
      }
      return acc;
    },{id:[],name:[]})

    const vmIdString = !_.isEmpty(vmId) ? _.join(vmId) : undefined;
    const fnIdString = !_.isEmpty(functionId) ? _.join(functionId) : undefined;
    const accountIdString = !_.isEmpty(accountId) ? _.join(accountId) : undefined;
    const vmNames = _.map(vmId, (vm) => {
      const vmFiltered = _.find(options, option => option.id === vm)?.name;
      return vmFiltered;
    });
    const fnNames = _.map(functionId, (fn) => {
      const fnFiltered = _.find(options, option => option.id === fn)?.name;
      return fnFiltered;
    });
    const accountNames = _.map(accountId, (ac) => {
      const accountIdFiltered = _.find(options, option => option.id === ac)?.name;
      return accountIdFiltered;
    });

    const groupName = _.find(options, option => option.id === groupId)?.name;

    return ({
      imageId: imageSelectAll === true ? "All" : data.id.join(),
      imageName: imageSelectAll === true ? "All" : data.id.join(),
      scope: config.scope,
      vmId: vmSelectAll === true ? "All" : vmIdString,
      vmName: vmSelectAll === true ? "All" : !_.isEmpty(vmNames) ? _.join(vmNames) : undefined,
      functionId: fnSelectAll === true ? "All" : fnIdString,
      functionName: fnSelectAll === true ? "All" : !_.isEmpty(fnNames) ? _.join(fnNames) : undefined,
      groupId: groupId,
      groupName,
      accountId: accountSelectAll === true ? "All" : accountIdString,
      accountName: accountSelectAll === true ? "All" : !_.isEmpty(accountNames) ? _.join(accountNames) : undefined,
      //send the property received in config
      ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
    });
  }, [options])

  const fetchSubProductsAndEnvironments = useCallback(async (configurations) => {
    const subProductPromises = [];
    const envPromises = [];
    const subProducts = {};
    const envs = {};
    configurations.forEach(config => {
        subProductPromises.push(SubProductAPI.getAllQuick(config.product_id).promise);
        envPromises.push(SubProductEnvironmentTypeAPI.getAllQuick(config.sub_product_id).promise);
    });

    Promise.all(subProductPromises).then((result) => {
        result.forEach((entry, i) => {
            subProducts[configurations[i].product_id] = entry;
        });
        setSubProductsMap(subProducts);
    }).catch((err) => console.error(err));

    Promise.all(envPromises).then((result) => {
        result.forEach((entry, i) => {
            envs[`${configurations[i].product_id}-${configurations[i].sub_product_name}`] = entry;
        });
        setEnvMap(envs);
    }).catch((err) => console.error(err));
}, []);

  const fetchProductSubproducts = useCallback(() => {
    setLoading(true);
    const { promise, cancel } = ProductAPI.getAllQuick();
    promise.then(async (_products) => {
            setProducts(_products);

        if(!editMode) {
            form.setFieldsValue({ configurations: props.formDefaultValues ? props.formDefaultValues : initialFormValues.configurations });
            setLoading(false);
        } else {
          if(props.record.product){
            fetchSubProductsAndEnvironments([props.record]);
          }
            const { product_name, repoName, vmId, functionId } = props.record;
            const _productId = (find(_products, (obj) => obj.name === product_name))?.id;
            const repos = repoName?.split(",");
            const vmIds = vmId?.split(",");
            const functionIds = functionId?.split(",");
            const extraFieldsData = parseExtraFieldsDataFromResponse(props.record);
            form.setFieldsValue({ configurations: [{
                ...props.record,
                ...extraFieldsData,
                subProduct: props.record.sub_product_name,
                product: _productId,
                repoName: repos,
                vmId: vmIds,
                functionId: functionIds
            }]});
            setLoading(false);
        }
    }).catch(err => {
        console.error(err);
        setLoading(false);
    });
    return cancel;
}, [form, editMode, fetchSubProductsAndEnvironments, props.record, parseExtraFieldsDataFromResponse,props.formDefaultValues]);

useEffect(() => {
  return fetchProductSubproducts();
}, [fetchProductSubproducts]);

const fetchEnv = (subProductId, envMapKey) => {
  SubProductEnvironmentTypeAPI.getAllQuick(subProductId).promise.then(envs => {
      setEnvMap((envMap) => ({
          ...envMap,
          [envMapKey]: envs
      }));
  }).catch(err => console.error(err))
}

const onProductSubProductCreation = async (productSubProductData, field, type) => {
  const {name, id} = productSubProductData
  const newValues = form.getFieldValue("configurations") || {};
  const productId = newValues[field.name]?.product
  newValues[field.name] = {
      ...newValues[field.name],
      ...(type === 'product' ? { product: id, subProduct: undefined } : {}),
      ...(type === 'subProduct' ? { subProduct: name } : {}),
      environment: undefined
  }

  if(type === "product") {
      setProducts(products => ([...products, { id, name }]))
  }

  if(type === 'subProduct') {
      setSubProductsMap((subProductsMap) => ({
          ...subProductsMap,
          [productId]: [...(subProductsMap[productId] || []),{id, name}]
      }));
      fetchEnv(id, `${productId}-${name}`)
  }

  form.setFieldsValue(newValues);
}

const onChange = (value, field, type) => {
  const { name } = field;
  const newValues = form.getFieldValue("configurations") || [];
  if (newValues[name]) {
      newValues[name] = {
          ...newValues[name],
          ...(type === 'product' && { subProduct: undefined }),
          environment: undefined
      }

      if(type === 'product' && !subProductsMap[value]) {
          SubProductAPI.getAllQuick(value).promise.then((subproducts) => {
              setSubProductsMap((subProductsMap) => ({
                  ...subProductsMap,
                  [value]: subproducts
              }));
          }).catch(err => console.error(err));
      }

      const envMapKey = `${newValues[name].product}-${value}`;
      if(type === 'subProduct' && !envMap[envMapKey]) {
          const subId = find(subProductsMap[newValues[name].product], (sub) => sub.name === value).id;
          fetchEnv(subId, envMapKey)
      }
  }

  form.setFieldsValue(newValues);
};
  
  const checkImageOrRepoEmpty = useCallback(() => {
    const data = form.getFieldsValue();
    let { configurations } = data;
    configurations = _.compact(configurations)
    const imageTabFields = ["imageId", "vmId", "functionId"];
    const imageTabSelectAllOptions = ["imageSelectAll", "vmSelectAll", "fnSelectAll"];
    const cspmTabFields = ["accountId"];
    let checkEmptyImageTabFields = false;
    let checkImageTabAllFieldsEmpty = false;
    let checkEmptySelectAll = false;
    let isCloudAccountFieldEmpty = false;
    let isCspmCloudAccountFieldsEmpty = false;
    for (const config of configurations) {
      checkImageTabAllFieldsEmpty = checkEmptyConfigFields(config, imageTabFields);
      isCloudAccountFieldEmpty = checkEmptyConfigFields(config, cspmTabFields);
      checkEmptySelectAll = imageTabSelectAllOptions.every(field => config[field] === false);
      isCspmCloudAccountFieldsEmpty = isCloudAccountFieldEmpty && config["accountSelectAll"] === false;
    }
    checkEmptyImageTabFields = !isOnPrem && currentTab === "image" && checkImageTabAllFieldsEmpty && checkEmptySelectAll;
    const checkCspmTabFields = !isOnPrem && currentTab === "cspm" && isCspmCloudAccountFieldsEmpty;
    return {
        validator:() => {
          if(checkEmptyImageTabFields) {
            return Promise.reject(`Aqua Image, VM Name and Function Names fields cannot be empty, please select either one or select 'All'`);
          } else if(checkCspmTabFields){
            return Promise.reject("Select Cloud Account or Select All")
          } else {
            return Promise.resolve();
          }
        }
    }
  }, [form, isOnPrem, currentTab]);

  const extraFields = useMemo(() => ([
    {
      name: 'scope',
      colSpan: '12',
      type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
      label: 'Scopes',
      labelCol: { span: 24 },
      disabled: disableProjectFields,
      required: currentTab === "image" && !props.isUnmapped,
      props: {
        api: fetchProjects,
        apiParams: PARAM_SCOPE_FIELD,
        onChange: onScopeChange,
      }
    },{
      colSpan: "12",
      render: (props) => (<div {...props} />),
    },
    {
    name: 'imageId',
    colSpan: '9',
    labelCol: { span: 24 },
    type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
    label: 'Aqua Image',
    disabled: disableProjectFields,
    rules:[checkImageOrRepoEmpty],
    props: {
      mode: "multiple",
      api: fetchProjects,
      onChange: onOptionalFieldsChange,
      setExtraProps: (fieldProps, currentField) => {
        const configurations = form.getFieldValue("configurations");
        const currentConfiguration = configurations[currentField.name] || {};
        const keyName = `${currentField.name}_${currentConfiguration.scope}`;
        if (!apiParamsRef.current[keyName]) {
          apiParamsRef.current[keyName] = { login_id: props.loginConfigId, project: currentConfiguration.scope }
        }
        return {
          apiParams: apiParamsRef.current[keyName],
          resetOptionsProp: apiParamsRef.current[keyName],
        }
      },
      autoFocus: !disableProjectFields
    }
  }, {
    name: "imageSelectAll",
    label: "All",
    type: FieldTypes.BOOLEAN,
    colSpan: "3",
    inline: true,
    props: {
      onChange: onOptionalFieldsAllSelectChange,
      style: {marginTop: "50%", marginBottom:"auto"}
    },
    disabled: disableProjectFields
  },{
      name: 'vmId',
      colSpan: '9',
      type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
      label: 'VM Names',
      labelCol: { span: 24 },
      disabled: disableProjectFields,
      rules:[checkImageOrRepoEmpty],
      props: {
        mode: "multiple",
        api: fetchVM,
        onChange: onOptionalFieldsChange,
        setExtraProps: (fieldProps, currentField) => {
          const configurations = form.getFieldValue("configurations");
          const currentConfiguration = configurations[currentField.name] || {};
          const keyName = `${currentField.name}_${currentConfiguration.scope}`;
          if (!vmApiParamRef.current[keyName]) {
            vmApiParamRef.current[keyName] = { login_id: props.loginConfigId, type:"vm", ...(!isOnPrem && {project: currentConfiguration.scope}) }
          }
          return {
            apiParams: vmApiParamRef.current[keyName],
            resetOptionsProp: vmApiParamRef.current[keyName],
          }
        },
      },
    },
    {
      name: "vmSelectAll",
      label: "All",
      type: FieldTypes.BOOLEAN,
      colSpan: "3",
      inline: true,
      props: {
        onChange: onOptionalFieldsAllSelectChange,
        style: {marginTop: "50%", marginBottom:"auto"}
      },
      disabled: disableProjectFields
    },
    {
      name: 'functionId',
      colSpan: '9',
      type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
      label: 'Function Names',
      labelCol: { span: 24 },
      disabled: disableProjectFields,
      rules:[checkImageOrRepoEmpty],
      props: {
        mode: "multiple",
        api: fetchFunctions,
        onChange: onOptionalFieldsChange,
        setExtraProps: (fieldProps, currentField) => {
          const configurations = form.getFieldValue("configurations");
          const currentConfiguration = configurations[currentField.name] || {};
          const keyName = `${currentField.name}_${currentConfiguration.scope}`;
          if (!fnApiParamRef.current[keyName]) {
            fnApiParamRef.current[keyName] = { login_id: props.loginConfigId, type:"function", ...(!isOnPrem && {project: currentConfiguration.scope}) }
          }
          return {
            apiParams: fnApiParamRef.current[keyName],
            resetOptionsProp: fnApiParamRef.current[keyName],
          }
        },
      },
    }, {
      name: "fnSelectAll",
      label: "All",
      type: FieldTypes.BOOLEAN,
      colSpan: "3",
      inline: true,
      props: {
        onChange: onOptionalFieldsAllSelectChange,
        style: {marginTop: "50%", marginBottom:"auto"}
      },
      disabled: disableProjectFields
    }, ...(!isEmpty(errorFields) ? [{type: FieldTypes.LABEL, colSpan: "18", render: () => <Alert message={<>You may not have permissions to fetch {errorFields.map((field, index) => <span key={index}>{field}{index !== errorFields.length -1 && ","}</span>)}</>} type="warning" className='m-b-sm'/>}] : [])
  ]), [form, fetchProjects, props.loginConfigId, disableProjectFields, isOnPrem, onScopeChange, checkImageOrRepoEmpty, fetchVM, fetchFunctions, onOptionalFieldsChange, onOptionalFieldsAllSelectChange, currentTab, props.isUnmapped, errorFields]);

  const configureTool = async () => {
    await form.validateFields();
    try {
    const data = form.getFieldsValue();
    setLoading(true);
    const { configurations } = data;
    let newConfigurations = configurations.map(config => {
        const { product, subProduct, id, repoName } = config;
        let { environment } = config;
        const repoNameString = !_.isEmpty(repoName) ? _.join(repoName) : undefined;
        const _product = find(products, (obj) => obj.id === product);
        if(hasDisabledEnvironment){
          environment = envValue;
        }
        const extraFieldsData = getExtraFieldsData(config);
        return {
            ...extraFieldsData,
            product_name: _product.name,
            sub_product_name: subProduct,
            environment,
            tool_name: props.toolName,
            login_id: props.loginConfigId,
            toolType: IntegrationType.PULL,
            id,
            repoName: repoNameString,
            repoId: repoNameString,
        }
    })
    newConfigurations = _.compact(newConfigurations);
    //send POST request when new mapping else, PUT request 
        if(editMode && !props.isUnmapped) {
            await ToolsIntegrationsAPI.updateGenericConfigurationByToolName(newConfigurations[0]).promise;
        } else{
            await ToolsIntegrationsAPI.postGenericToolsConfiguration(newConfigurations).promise;
        }
        message.success('Configuration saved successfully');
        props.afterClose();
        props.onUpdateConfigComponent && props.onUpdateConfigComponent();
    } catch (e) {
        setLoading(false);
        throw e;
    }
};

  const _modalProps = {
    width: MODAL_SIZE_LARGE,
    style: { top: 80 },
    onOk: configureTool,
    onCancel: props.afterClose,
    disabled: !hasPermission(editMode ? PermissionSetMap.EDIT_TOOL_CONFIG : PermissionSetMap.WRITE_TOOLS),
    title: `Map ${props.toolName} Projects`
};

  return (
    <BaseModal
            modalProps={_modalProps}
            ref={ref}
            loading={loading}
        >
            <Form
                form={form}
                initialValues={initialFormValues.configurations}
            >
                <AquaseqTabbedFields
                    {...props}
                    disableProjectFields={disableProjectFields}
                    commonProperty={commonAquasecFieldsProperty}
                    name="configurations"
                    form={form}
                    setOptionsFromPagination={setOptionsFromPagination}
                    extraFields={extraFields}
                    checkEmptyFields={checkImageOrRepoEmpty}
                    addButtonDisabled={props.disableAddConfiguration}
                    getGroups={getGroups}
                    getCloudAccounts={getCloudAccounts}
                    isOnPrem={isOnPrem}
                    onOptionalFieldsAllSelectChange={onOptionalFieldsAllSelectChange}
                    onOptionalFieldsChange={onOptionalFieldsChange}
                    onGroupChange={onGroupChange}
                    handleTabChange={handleTabChange}
                    currentTab={currentTab}
                    editMode={editMode}
                    fieldRows={
                        [
                            {
                                name: 'product',
                                type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
                                colSpan: 8,
                                label: <>ArmorCode <Vocabulary name="product" isPlural={true} /></>,
                                props: {
                                    getFormListOptions: () => {
                                        return products.map(item => ({label: item.name, value: item.id}))
                                    },
                                    onChange: (value, field) => onChange(value, field, 'product'),
                                    dropdownType: 'product',
                                    showAddButton: true,
                                    dontFetchProductSubProduct: true,
                                    onCreate: (createdProduct, field) => {
                                        onProductSubProductCreation(createdProduct, field, 'product')
                                    }
                                },
                                rules: [{ required: true, message: <><Vocabulary name="product" isPlural={true} /> is required</> }],
                                disabled: disableMapping,
                                ...commonAquasecFieldsProperty
                            },
                            {
                                name: 'subProduct',
                                type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
                                colSpan: 8,
                                label: <>ArmorCode <Vocabulary name="subProduct" isPlural={true} /></>,
                                props: {
                                    getFormListOptions: (fieldKey) => {
                                        const selectedProduct = form.getFieldValue("configurations")[fieldKey]?.product;
                                        return findOptionsFromMap(selectedProduct, subProductsMap);
                                    },
                                    getProductId: (listIndex) => {
                                        const configData = form.getFieldValue("configurations")
                                        return configData[listIndex]?.product
                                    },
                                    dontFetchProductSubProduct: true,
                                    dropdownType: 'subProduct',
                                    onCreate: (createdSubproduct, field) => {
                                        onProductSubProductCreation(createdSubproduct, field, 'subProduct')
                                    },
                                    onChange: (value, field) => onChange(value, field, 'subProduct')
                                },
                                rules: [{ required: true, message: <><Vocabulary name="subProduct" isPlural={true} /> is required</> }],
                                disabled: disableMapping,
                                ...commonAquasecFieldsProperty
                            },
                            ...(hasDisabledEnvironment ? [] : [{
                              name: 'environment',
                              type: 'Select',
                              colSpan: 8,
                              label: 'ArmorCode Environment',
                              props: {
                                  getFormListOptions: (fieldKey) => {
                                      const selectedProduct = form.getFieldValue("configurations")[fieldKey]?.product;
                                      const selectedSubProduct = form.getFieldValue("configurations")[fieldKey]?.subProduct;
                                      return findOptionsFromMap(`${selectedProduct}-${selectedSubProduct}`, envMap, true);
                                  },
                              },
                              rules: [{ required: true, message: 'Environment is required' }],
                              disabled: disableMapping,
                              ...commonAquasecFieldsProperty
                          }])
                        ]
                    }
                />
            </Form>
        </BaseModal>
        // :
        // <GenericMappingForm
        // ref={ref}
        // form={form}
        // getExtraFieldsData={getExtraFieldsData}
        // parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
        // extraFields={extraFields}
        // {...props}
        // />
  )
})

export default AquasecMappingForm;

function AquaseqTabbedFields({ commonProperty, name, form, editMode, deleteIconTooltip, addButtonDisabled, buttonLabel, fieldRows, fetchProjects, setOptionsFromPagination, setImageOptionsFromPagination, setHostOptionsFromPagination,disableProjectFields, ...props }){
  const accountApiParamRef = useRef({});
  const fetchRepos = useCallback((params) => {
      const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, params);
      promise.then((data) =>{
          setOptionsFromPagination(data.projects, params.type)
      })
      return { promise, cancel }
    }, [props.toolName, setOptionsFromPagination]);

  const codeRepoFields = useMemo(() => ([
      {
          name: 'repoName',
          colSpan: 12,
          labelCol: { span: 24 },
          type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
          label: 'Repositories List',
          required: props.currentTab === "supplyChain",
          props: {
              mode:"multiple",
              api:fetchRepos,
              apiParams: { login_id: props.loginConfigId, type:"repo" },
          },
          disabled: disableProjectFields
      }
  ]), [props.loginConfigId, fetchRepos, disableProjectFields, props.currentTab]);

  const cspmFields = useMemo(() => ([
    {
      name: 'groupId',
      colSpan: '8',
      type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
      label: 'Aqua Group',
      labelCol: { span: 24 },
      disabled: disableProjectFields,
      required: props.currentTab === "cspm" && !props.isUnmapped,
      props: {
        api: props.getGroups,
        onChange: props.onGroupChange
      }
    },
    {
      name: 'accountId',
      colSpan: '8',
      type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
      label: 'Cloud Account',
      labelCol: { span: 24 },
      disabled: disableProjectFields,
      rules:[props.checkEmptyFields],
      props: {
        mode: "multiple",
        api: props.getCloudAccounts,
        onChange: props.onOptionalFieldsChange,
        setExtraProps: (fieldProps, currentField) => {
          const configurations = form.getFieldValue("configurations");
          const currentConfiguration = configurations[currentField.name] || {};
          const keyName = `${currentField.name}_${currentConfiguration.groupId}`;
          if (!accountApiParamRef.current[keyName]) {
            accountApiParamRef.current[keyName] = { login_id: props.loginConfigId, type:"account", ...(!props.isOnPrem && {project: currentConfiguration.groupId}) }
          }
          return {
            apiParams: accountApiParamRef.current[keyName],
            resetOptionsProp: accountApiParamRef.current[keyName],
          }
        },
      },
    },
    {
      name: "accountSelectAll",
      label: "Select All Cloud Accounts",
      type: FieldTypes.BOOLEAN,
      colSpan: "8",
      inline: true,
      props: {
        onChange: props.onOptionalFieldsAllSelectChange,
        style: {marginTop: "20%", marginBottom:"auto"}
      },
      disabled: disableProjectFields
    }
  ]), [disableProjectFields, props.getGroups, form, props.getCloudAccounts, props.isOnPrem, props.loginConfigId, props.onOptionalFieldsAllSelectChange, props.onOptionalFieldsChange, props.onGroupChange, props.currentTab, props.checkEmptyFields, props.isUnmapped]);
  
  return <Form.List name={name}>
      {(fields, { add, remove }) => (
          <>
              {fields.map((currentRowField) => {
                  return <Alert
                  className="m-b-sm"
                  key={currentRowField.key}
                  type="info"
                  description={<>
                                <Tabs defaultActiveKey='image' onChange={props.handleTabChange}>
                                    <TabPane tab="Workload Protection" key="image" >
                                        {generateFormFields(props.extraFields, 24, currentRowField)}
                                    </TabPane>
                                    <TabPane tab="Supply Chain Security" key="supplyChain">
                                      {generateFormFields(codeRepoFields, 24, currentRowField)}
                                    </TabPane>
                                    <TabPane tab={"CSPM"} key="cspm">
                                      {generateFormFields(cspmFields, 24, currentRowField)}
                                    </TabPane>
                                </Tabs>
                          <div className="d-flex flex-column align-items-center">
                              <Typography.Text>Map To</Typography.Text>
                              <ArrowDownOutlined className='p-xs' />
                          </div>

                          {generateFormFields(fieldRows, 24, currentRowField)}

                          {!editMode &&
                              <div span={24} className="m-b-0 text-right" >
                                  <Button type="dashed" 
                                      className={classNames({ disabled: fields.length === 1 }, 'danger')} 
                                      icon={<DeleteIcon/>} disabled={addButtonDisabled} 
                                      onClick={() => {
                                          if (fields.length !== 1) {
                                             remove(currentRowField.name);
                                          }
                                  }} >
                                      {buttonLabel || "Delete Row"}
                                  </Button>
                              </div>
                          }
                      </>}
                  />
              })}
              {!editMode && 
                  <div span={24} className="m-b-0 p-l-xs p-r-xs text-right" >
                      <Button 
                          type="dashed" 
                          onClick={() => add()} 
                          icon={<PlusOutlined />} 
                          disabled={addButtonDisabled}
                      >
                          {buttonLabel || "Add Mapping"}
                      </Button>
                  </div>
              }
          </>
      )}
  </Form.List>
}

const checkEmptyConfigFields = (config, fields) => {
 return fields.every(field => _.isEmpty(config[field]));
}